/**
 * View model: turns a `BidResult` into display-ready, cent-rounded numbers.
 * DOM-free (unit-tested).
 *
 * Money rule: every monthly bill is rounded to cents first; term totals are
 * the sum of rounded months, so the printed numbers always add up.
 */
import type { TermMonths } from "../config";
import type { AssumptionEcho, BidResult, WaterfallStepId } from "../engine/bid";
import { roundCents, type TermBills } from "../engine/billing";

export const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;
export const MONTHS_LONG = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
] as const;

export interface TermView {
  term: TermMonths;
  /** Calendar month index per term month. */
  months: number[];
  todayMonths: number[];
  octopusMonths: number[];
  todayTotal: number;
  octopusTotal: number;
  savingsTotal: number;
  todayAvg: number;
  octopusAvg: number;
  savingsAvg: number;
  waterfall: WaterfallRow[];
}

export interface WaterfallRow {
  id: WaterfallStepId;
  label: string;
  emoji: string;
  total: number;
  avg: number;
}

const sumRounded = (b: TermBills): number => roundCents(b.months.reduce((a, m) => a + roundCents(m.total), 0));

/** V1 has no solar; the battery's bill impact is shown as a perk, not a waterfall step. */
const HIDDEN_STEPS: ReadonlySet<WaterfallStepId> = new Set(["solar", "battery"]);

export function termView(bid: BidResult, term: TermMonths): TermView {
  const A = bid.scenarios.A.byTerm[term];
  const C = bid.scenarios.C.byTerm[term];
  const todayTotal = sumRounded(A);
  const octopusTotal = sumRounded(C);
  const savingsTotal = roundCents(todayTotal - octopusTotal);

  // Waterfall: round each visible step, then plug the residual into the
  // largest step so the rows add up exactly to the headline savings.
  const rows: WaterfallRow[] = bid.waterfall
    .filter((s) => !HIDDEN_STEPS.has(s.id) || Math.abs(s.byTerm[term].total) >= 0.005)
    .map((s) => ({ id: s.id, label: s.label, emoji: s.emoji, total: roundCents(s.byTerm[term].total), avg: 0 }));
  const residual = roundCents(savingsTotal - rows.reduce((a, r) => a + r.total, 0));
  if (residual !== 0 && rows.length) {
    const big = rows.reduce((m, r) => (Math.abs(r.total) > Math.abs(m.total) ? r : m), rows[0]!);
    big.total = roundCents(big.total + residual);
  }
  for (const r of rows) r.avg = roundCents(r.total / term);

  return {
    term,
    months: A.months.map((m) => m.month),
    todayMonths: A.months.map((m) => roundCents(m.total)),
    octopusMonths: C.months.map((m) => roundCents(m.total)),
    todayTotal,
    octopusTotal,
    savingsTotal,
    todayAvg: roundCents(todayTotal / term),
    octopusAvg: roundCents(octopusTotal / term),
    savingsAvg: roundCents(savingsTotal / term),
    waterfall: rows,
  };
}

// ── Assumptions filtering ─────────────────────────────────────────────────

/** Solar-only assumptions: hidden everywhere in V1 (no solar). */
export function isSolarOnly(a: AssumptionEcho): boolean {
  return a.key.startsWith("octopus.buyback.") || a.key.startsWith("solar.") || a.key === "region.yieldKwhPerKw";
}

/** Rep-only: VPP program value and its inputs. Never in the customer view or print. */
export function isRepOnly(a: AssumptionEcho): boolean {
  return a.key.startsWith("octopus.vpp.");
}

/** Assumptions for the ⚙️ drawer (rep). */
export function drawerAssumptions(bid: BidResult): AssumptionEcho[] {
  return bid.assumptions.filter((a) => !isSolarOnly(a));
}

/** Assumptions safe for the customer printout. */
export function printAssumptions(bid: BidResult, term: TermMonths): AssumptionEcho[] {
  const engineKeep = new Set(["ev.annualMiles", "ev.kWhPerMile", "ev.chargeWindow", "nest.coolingReductionPct", "battery.capacityKwh"]);
  return bid.assumptions.filter((a) => {
    if (isSolarOnly(a) || isRepOnly(a)) return false;
    if (a.key.startsWith("octopus.energyRate.")) return a.key === `octopus.energyRate.${term}`;
    if (a.group === "region") return false;
    if (a.group === "engine") return engineKeep.has(a.key);
    return true;
  });
}

export interface RateFlag {
  key: string;
  text: string;
}

/** ⚠ badges for temporary / placeholder rates that affect this bid. */
export function rateFlags(bid: BidResult): RateFlag[] {
  const flags: RateFlag[] = [];
  for (const a of bid.assumptions) {
    if (isSolarOnly(a) || isRepOnly(a)) continue;
    if (a.key === "tdu.volumetricRate" && !a.overridden && /temporary/i.test(a.note ?? "")) {
      flags.push({ key: a.key, text: `${a.label} is a temporary rate (subject to refund)` });
    } else if (a.placeholder) {
      flags.push({ key: a.key, text: `${a.label} is a placeholder pending confirmation` });
    }
  }
  return flags;
}

// ── Formatting ────────────────────────────────────────────────────────────

const usdFmt = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
const usdWhole = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const numFmt = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });

export function usd(n: number): string {
  return usdFmt.format(roundCents(n));
}
export function usdRound(n: number): string {
  return usdWhole.format(n);
}
export function kwh(n: number): string {
  return `${numFmt.format(n)} kWh`;
}

/** Human-readable assumption value with its unit. */
export function formatAssumption(a: Pick<AssumptionEcho, "value" | "unit" | "key">, value: AssumptionEcho["value"] = a.value): string {
  const v = value;
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "string") return v.replace(/_/g, " ");
  if (Array.isArray(v)) {
    if (a.key === "ev.chargeWindow") return `${String(v[0]).padStart(2, "0")}:00–${String(v[1]).padStart(2, "0")}:00`;
    return v.join(", ");
  }
  const n = v as number;
  switch (a.unit) {
    case "$/kWh":
      return `${trim(n * 100, 4)}¢/kWh`;
    case "$/month":
      return `${usd(n)}/mo`;
    case "$/kWh·month":
      return `${usd(n)} per kWh·mo`;
    case "fraction":
    case "fraction of cooling":
      return `${trim(n * 100, 1)}%`;
    default:
      return `${Math.abs(n) >= 1000 ? numFmt.format(n) : trim(n, 4)} ${a.unit}`;
  }
}

/**
 * Effective date + source to display. When the rep picked a published
 * alternate (e.g. Oncor PUCT 6.0295¢), show that alternate's own provenance.
 */
export function provenance(a: AssumptionEcho): { date: string; source: string } {
  if (a.overridden && typeof a.value === "number") {
    const alt = a.alternates?.find((x) => x.value === a.value);
    if (alt) return { date: alt.effectiveDate, source: `${alt.label}: ${alt.source} (rep-selected alternate)` };
  }
  return { date: a.effectiveDate ?? (a.group === "customer" ? "Your bill" : "—"), source: a.source };
}

/**
 * Engine warnings for the rep drawer. V1 has no solar in the UI, so the
 * engine's battery-without-solar note is restated without mentioning solar.
 */
export function repNotes(bid: BidResult): string[] {
  return bid.warnings.map((w) =>
    /without solar/i.test(w)
      ? "Battery-only (V1): held for backup and VPP dispatch. Customer bill impact $0; its value is the VPP program value retained by Octopus."
      : w,
  );
}

function trim(n: number, dp: number): string {
  return String(Number(n.toFixed(dp)));
}

/** Calendar year a plan starting in `startMonth` begins (this year, or next if the month has passed). */
export function termStartYear(startMonth: number, today: Date = new Date()): number {
  return today.getFullYear() + (startMonth < today.getMonth() ? 1 : 0);
}

/** "Nov 2026 – Oct 2027" style label for a term starting at `startMonth`. */
export function termRangeLabel(startMonth: number, term: number, today: Date = new Date()): string {
  const startYear = termStartYear(startMonth, today);
  const endIdx = startMonth + term - 1;
  const endMonth = endIdx % 12;
  const endYear = startYear + Math.floor(endIdx / 12);
  return `${MONTHS_SHORT[startMonth]} ${startYear} – ${MONTHS_SHORT[endMonth]} ${endYear}`;
}
